// src/components/AdvisorList.tsx
import React, { useEffect, useState } from 'react';
import Footer from './Footer';
import { Advisor, FilterOptions } from '../../types';
import SearchFilters from '../SearchFilters';
import AdvisorCard from '../AdvisorCard';
import AdvisorModal from '../AdvisorModal';
import { advisors as staticAdvisors } from '../../data/advisorsData';

const AdvisorList: React.FC = () => {
  const [advisors, setAdvisors] = useState<Advisor[]>([]);
  const [filteredAdvisors, setFilteredAdvisors] = useState<Advisor[]>([]);
  const [selectedAdvisor, setSelectedAdvisor] = useState<Advisor | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAdvisors();
  }, []);

  const fetchAdvisors = async (filters?: FilterOptions) => {
    try {
      setLoading(true);
      setError(null);

      let data = [...staticAdvisors];

      if (filters) {
        const {
          location,
          specialization,
          minRating,
          maxRate,
        } = filters;

        if (location) {
          data = data.filter(advisor =>
            advisor.location.toLowerCase().includes(location.toLowerCase())
          );
        }

        if (specialization) {
          data = data.filter(advisor =>
            advisor.specializations.some(spec =>
              spec.toLowerCase().includes(specialization.toLowerCase())
            )
          );
        }

        if (minRating !== undefined && minRating !== null) {
          data = data.filter(advisor =>
            advisor.rating !== undefined && advisor.rating >= minRating
          );
        }

        if (maxRate !== undefined && maxRate !== null) {
          data = data.filter(advisor =>
            advisor.hourlyRate !== undefined && advisor.hourlyRate <= maxRate
          );
        }
      }

      // Sort featured advisors to the top
        const sortedData = data.sort((a, b) => {
          const isAFeatured = a.featured ? 1 : 0;
          const isBFeatured = b.featured ? 1 : 0;
          return isBFeatured - isAFeatured; // Featured first
        });

        setAdvisors(sortedData);
        setFilteredAdvisors(sortedData);
        
    } catch (err) {
      console.error('Error loading advisors:', err);
      setError('Failed to load advisors. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters: FilterOptions) => {
    fetchAdvisors(filters);
  };

  const handleAdvisorClick = (advisor: Advisor) => {
    setSelectedAdvisor(advisor);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAdvisor(null);
  };

  return (
    <>
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl md:text-5xl">
          Finding a good financial advisor is
          <span className="text-primary-600"> really really hard</span> in India
        </h1>
        <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
          Most Indians struggle to find quality financial guidance
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="text-4xl font-bold text-primary-600">50%</div>
            <div className="text-gray-600 mt-2">of bank relationship managers admit to mis-selling financial products to meet targets.</div>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="text-4xl font-bold text-primary-600">95%</div>
            <div className="text-gray-600 mt-2">of Indians make the mistake of buying insurance policies for investment purposes.</div>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="text-4xl font-bold text-primary-600">71%</div>
            <div className="text-gray-600 mt-2">of urban Indians over 45 worry about their financial security post-retirement.</div>
          </div>
        </div>

        {/* Solution Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            We're solving this by building India's most
            <span className="text-primary-600"> trusted network of financial advisors</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Verified and Experienced Advisors
              </h3>
              <p className="text-gray-600">
                Carefully vetted professionals who understand your unique financial needs and goals.
              </p>
            </div>

            <div className="p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">💼</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Comprehensive Financial Solutions
              </h3>
              <p className="text-gray-600">
                All your financial planning needs addressed in one place by expert advisors.
              </p>
            </div>

            <div className="p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🤝</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Transparent and Trustworthy
              </h3>
              <p className="text-gray-600">
                A convenient platform built on transparency and trust to serve you better.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            Find
            <span className="text-primary-600"> Trusted Financial Advisors</span>
          </h2>
        </div>
      </div>
      
      <SearchFilters onFilterChange={handleFilterChange} />

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-2 text-gray-600">Loading advisors...</p>
        </div>
      )}

      {error && (
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-md p-4 max-w-md mx-auto">
            <p className="text-red-600">{error}</p>
            <button onClick={() => fetchAdvisors()} className="mt-2 btn-primary">
              Try Again
            </button>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="mb-6">
            <p className="text-gray-600">
              {filteredAdvisors.length} advisor{filteredAdvisors.length !== 1 ? 's' : ''} found
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAdvisors.map((advisor) => (
              <AdvisorCard
                key={advisor.id}
                advisor={advisor}
                onClick={() => handleAdvisorClick(advisor)}
              />
            ))}
          </div>

          {filteredAdvisors.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No advisors found matching your criteria.</p>
              <p className="text-gray-400 mt-2">Try adjusting your filters to see more results.</p>
            </div>
          )}
        </>
      )}

      {selectedAdvisor && (
        <AdvisorModal
          advisor={selectedAdvisor}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}

      <Footer />
    </>
  );
};

export default AdvisorList;
